import React, { ComponentProps } from 'react';
import { ProgressBreadcrumb } from './ProgressBreadcrumb';

type ProgressBreadcrumbProps = ComponentProps<typeof ProgressBreadcrumb>;

export enum ProgressBreadcrumbListTheme {
    White = 'white',
    Blue = 'blue',
    Red = 'red',
    Gray = 'gray',
}

export enum ProgressBreadcrumbCompletedDisplayStyle {
    Number,
    Checkmark,
}

// Same mobile rule as isMobileScreen() in @apply/ui (which can't be imported here: @apply/ui depends on this package)
const MOBILE_SCREEN_WIDTH = 450;
const isMobileScreen = () => typeof window !== 'undefined' && window.screen.width < MOBILE_SCREEN_WIDTH;

export const ProgressBreadcrumbList: React.FC<{
    id: string;
    crumbs: ProgressBreadcrumbProps[];
    theme: ProgressBreadcrumbListTheme;
    activeCrumbNumber: number;
    orientation?: 'horizontal' | 'vertical';
    width?: string;
    height?: string;
    completedCrumbDisplayStyle?: ProgressBreadcrumbCompletedDisplayStyle;
}> = ({
    id,
    crumbs,
    theme,
    activeCrumbNumber,
    orientation = 'vertical',
    width = 'auto',
    height = 'auto',
    completedCrumbDisplayStyle = ProgressBreadcrumbCompletedDisplayStyle.Number,
}) => {
    const setStyle = {
        width: `${width}`,
        height: `${height}`,
        '--crumbCount': `${crumbs.length}`,
    };

    // Progress bars are only shown in the vertical layout, and never on mobile screens
    const showProgressBars = orientation === 'vertical' && !isMobileScreen();

    // When the last crumb is current and shows a progress bar, the background connector line must stop
    // at that crumb's bubble rather than running down alongside the progress bar.
    const lastCrumb = crumbs?.[crumbs.length - 1];
    const progressOnLastCrumb =
        showProgressBars && !!lastCrumb?.progressBarInfo && lastCrumb.number == activeCrumbNumber;

    return (
        <>
            <div
                id={`progressBreadcrumbList_${id}`}
                className={`progressBreadcrumbList ${orientation}${progressOnLastCrumb ? ' progressOnLastCrumb' : ''}`}
                style={setStyle}
            >
                {crumbs?.map((crumb) => (
                    <ProgressBreadcrumb
                        id={crumb.id}
                        key={crumb.id}
                        number={crumb.number}
                        currentNumber={activeCrumbNumber}
                        hasProgressed={activeCrumbNumber >= crumb.number}
                        handleClick={crumb.handleClick}
                        enabled={crumb.enabled}
                        disabled={crumb.disabled}
                        theme={theme}
                        completedCrumbDisplayStyle={completedCrumbDisplayStyle}
                        progressBarInfo={showProgressBars ? crumb.progressBarInfo : undefined}
                        hasError={crumb.hasError}
                        errorHint={crumb.errorHint}
                    >
                        {crumb.children}
                    </ProgressBreadcrumb>
                ))}
            </div>
        </>
    );
};
